import { useCallback, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button, Modal } from "@similarweb/ui-components";

import ImportFromGSC from "app/pages/digital-marketing/keywords-campaigns/shared/gsc-import/ImportFromGSC";
import type {
    FixedGscConfig,
    GscStateChangePayload,
    GscSyncInput,
    StandardGscDraft,
} from "app/pages/digital-marketing/keywords-campaigns/shared/gsc-import/types";

type Props = {
    isOpen: boolean;
    fixedConfig: FixedGscConfig;
    initialDraft?: StandardGscDraft | null;
    onClose: () => void;
    onSubmit: (input: GscSyncInput) => void;
};

const MODAL_STYLES = { content: { width: "620px", maxHeight: "90vh" } };

// Modal for importing keywords from GSC, constraining the website and Google market context
// via fixedConfig derived from the campaign's website and market settings.
export const ImportGscModal = ({ isOpen, fixedConfig, initialDraft, onClose, onSubmit }: Props) => {
    const { translate } = useTranslate();
    const [gscState, setGscState] = useState<GscStateChangePayload | null>(null);

    const handleCancel = useCallback(() => {
        setGscState(null);
        onClose();
    }, [onClose]);

    const handleSubmit = useCallback(() => {
        if (!gscState?.isValid) {
            return;
        }
        onSubmit(gscState.input);
        setGscState(null);
        onClose();
    }, [onClose, onSubmit, gscState]);

    return (
        <Modal
            isOpen={isOpen}
            data-automation="keywords-step-gsc-import-modal"
            onRequestClose={handleCancel}
            customStyles={MODAL_STYLES}
        >
            <Modal.Header>
                <Modal.Title>{translate("rankTracker.wizard.gsc.header")}</Modal.Title>
            </Modal.Header>
            <Modal.Content>
                <ImportFromGSC
                    fixedConfig={fixedConfig}
                    initialDraft={initialDraft}
                    onStateChange={setGscState}
                />
            </Modal.Content>
            <Modal.Footer withBorder>
                <Button
                    type="flat"
                    data-automation="keywords-step-gsc-import-cancel"
                    onClick={handleCancel}
                >
                    {translate("global.cancel")}
                </Button>
                <Button
                    type="primary"
                    data-automation="keywords-step-gsc-import-submit"
                    isDisabled={!gscState?.isValid}
                    onClick={handleSubmit}
                >
                    {translate("common.submit")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
